/**
 * Saker's editors and questions (issue #286, handoff S07, S08, S10, S11, S12, S14, S20): each a modal dialog on
 * Radix (focus kept inside, Escape and the close button as safe ways out, focus back on the opener), 500 px wide,
 * its footer pinned. A dialog with typed values asks before it closes: keep editing, or discard and go back to the
 * same task with nothing written. Every value is checked here before anything is sent; the page writes through
 * its own services and the dialog shows the answer, keeping the draft on a refusal. Styles: SakerDialogs.module.css.
 */
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { Button } from '@/ui';
import { Icon } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import css from './SakerDialogs.module.css';
import type { ReviewCase } from './reviewBar.types';
import { hoursMin } from './saker';

/** What a failed write said, shown in the dialog with the draft kept (S18). */
export interface WriteProblem {
  message: string;
  /** a conflict: the fields that moved and what they hold now */
  changed?: { label: string; value: string }[] | undefined;
}

function Frame({
  open,
  title,
  sub,
  dirty,
  busy,
  onClose,
  children,
  footer,
  onSubmit,
  testid,
  cancel = true,
}: {
  cancel?: boolean;
  open: boolean;
  title: string;
  sub?: ReactNode;
  dirty?: boolean;
  busy?: boolean;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
  onSubmit?: (e: FormEvent) => void;
  testid?: string;
}) {
  const [asking, setAsking] = useState(false);
  const keep = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) setAsking(false);
  }, [open]);
  useEffect(() => {
    if (asking) keep.current?.focus();
  }, [asking]);
  // every way out arrives here: typed values are asked about first (S08); a save in flight holds the dialog
  const attempt = () => {
    if (busy) return;
    if (dirty) setAsking(true);
    else onClose();
  };
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && attempt()}>
      <Dialog.Portal>
        <Dialog.Overlay className="scrim" />
        <Dialog.Content className={css.dlg} aria-describedby={undefined} data-testid={testid}>
          <form onSubmit={onSubmit} noValidate className={css.form}>
            <header className={css.head}>
              <div>
                <Dialog.Title asChild>
                  <h2>{title}</h2>
                </Dialog.Title>
                {sub && <p>{sub}</p>}
              </div>
              <button type="button" className={css.x} aria-label={T('Lukk')} onClick={attempt}>
                <Icon name="close" size={18} strokeWidth={1.7} />
              </button>
            </header>
            <div className={css.body}>{children}</div>
            {asking && (
              <div className={css.dirty} role="alert" data-testid="saker-dirty">
                <strong>{T('Du har ulagrede endringer.')}</strong>
                <p>{T('Vil du fortsette å redigere eller forkaste endringene?')}</p>
                <div className={css.row}>
                  <button
                    ref={keep}
                    type="button"
                    className="btn"
                    onClick={() => setAsking(false)}
                    data-testid="saker-keep"
                  >
                    {T('Fortsett å redigere')}
                  </button>
                  <Button tone="dgr" onClick={onClose} data-testid="saker-discard">
                    {T('Forkast endringene')}
                  </Button>
                </div>
              </div>
            )}
            <footer className={css.foot}>
              {/* Avbryt is a way out like Escape and the cross: typed values are asked about first (S08) */}
              {cancel && (
                <Button onClick={attempt} disabled={busy} data-testid="saker-cancel">
                  {T('Avbryt')}
                </Button>
              )}
              {footer}
            </footer>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Problem({ p }: { p: WriteProblem | null }) {
  if (!p) return null;
  return (
    <div className={css.problem} role="alert" data-testid="saker-problem">
      <Icon name="warn" size={18} strokeWidth={1.7} />
      <div>
        <strong>
          {p.changed ? T('Registreringen er endret av en annen.') : T('Endringen ble ikke lagret.')}
        </strong>
        <p>{p.message}</p>
        {p.changed && (
          <dl className={css.facts}>
            {p.changed.map((c) => (
              <div key={c.label}>
                <dt>{c.label}</dt>
                <dd>{c.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <p>{T('Det du skrev er beholdt.')}</p>
      </div>
    </div>
  );
}

function Facts({ rows }: { rows: [string, string][] }) {
  return (
    <dl className={css.facts}>
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const clock = (iso: string | null | undefined) => oslo(iso)?.time ?? '';
const minutesBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 60_000);

export const PAUSE_REASONS = [
  'Avklart med den ansatte',
  'Bekreftet av ansvarlig på jobb',
  'Glemte å registrere pausen',
];

/**
 * «Rett pausen» (S07): the clocked span, when the break started and how long it was, and why. Gross, break and net
 * agree as typed; the break lies inside the clocked span (a night past midnight named by its date); a reason is
 * required. Nothing is deducted on its own here: the break written is the one typed.
 */
export function PauseDialog({
  c,
  open,
  busy,
  problem,
  onClose,
  onSave,
}: {
  c: ReviewCase;
  open: boolean;
  busy: boolean;
  problem: WriteProblem | null;
  onClose: () => void;
  onSave: (v: { lunchIn: string | null; lunchOut: string | null; reason: string }) => void;
}) {
  const p = c.pause;
  const startTime = p?.lunchIn ? clock(p.lunchIn) : '';
  const startMin = p?.lunchIn && p.lunchOut ? String(minutesBetween(p.lunchIn, p.lunchOut)) : '';
  const [at, setAt] = useState(startTime);
  const [min, setMin] = useState(startMin);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<{ at?: string; min?: string; reason?: string }>({});
  if (!p) return null;
  const gross = minutesBetween(p.punchIn, p.punchOut);
  const recorded = p.lunchIn && p.lunchOut ? minutesBetween(p.lunchIn, p.lunchOut) : 0;
  const n = Number(min);
  const validMin = min !== '' && Number.isInteger(n) && n >= 0 && n <= gross;
  const dirty = at !== startTime || min !== startMin || !!reason;
  const inDay = oslo(p.punchIn);
  const outDay = oslo(p.punchOut);
  /** the break's start as an instant: the clock-in's day, or the next when the shift ran past midnight */
  const startIso = () => {
    if (!inDay || !outDay || !/^\d\d:\d\d$/.test(at)) return null;
    const same = osloISO(inDay.date, at);
    if (Date.parse(same) >= Date.parse(p.punchIn)) return same;
    return outDay.date > inDay.date ? osloISO(outDay.date, at) : same;
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const x: typeof err = {};
    if (!validMin) x.min = T('Pausen må være mellom 0 og {n} hele minutter.', { n: gross });
    let lunchIn: string | null = null;
    let lunchOut: string | null = null;
    if (validMin && n > 0) {
      const s = startIso();
      if (!s) x.at = T('Skriv inn når pausen startet.');
      else if (Date.parse(s) < Date.parse(p.punchIn) || Date.parse(s) + n * 60_000 > Date.parse(p.punchOut))
        x.at = T('Pausen må ligge mellom {a} og {b}.', { a: clock(p.punchIn), b: clock(p.punchOut) });
      else {
        lunchIn = s;
        lunchOut = new Date(Date.parse(s) + n * 60_000).toISOString();
      }
    }
    if (!reason) x.reason = T('Velg en begrunnelse.');
    setErr(x);
    if (!x.at && !x.min && !x.reason) onSave({ lunchIn, lunchOut, reason });
  };
  return (
    <Frame
      open={open}
      title={T('Rett pausen')}
      sub={[c.person?.name, c.dateText ?? c.dateLabel].filter(Boolean).join(', ')}
      dirty={dirty}
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      testid="saker-pause"
      footer=<Button tone="pri" type="submit" disabled={busy} data-testid="saker-pause-save">
        {busy ? T('Lagrer …') : T('Lagre pause')}
      </Button>
    >
      <Facts
        rows={[
          [T('Registrert tid'), `${clock(p.punchIn)}–${clock(p.punchOut)}`],
          [T('Tid mellom start og slutt'), hoursMin(gross)],
          [T('Registrert pause'), hoursMin(recorded)],
        ]}
      />
      <label className={css.field}>
        <span>{T('Faktisk pause i minutter')}</span>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={gross}
          step={1}
          value={min}
          onChange={(e) => setMin(e.target.value)}
          aria-invalid={err.min ? true : undefined}
          aria-describedby="pause-min-help"
          data-testid="saker-pause-min"
        />
        <small id="pause-min-help">
          {T('Skriv pausen som faktisk ble tatt. Ingenting trekkes automatisk her.')}
        </small>
        {err.min && <small className={css.err}>{err.min}</small>}
      </label>
      {(n > 0 || !min) && (
        <label className={css.field}>
          <span>{T('Pausen startet')}</span>
          <input
            type="time"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            aria-invalid={err.at ? true : undefined}
            data-testid="saker-pause-at"
          />
          {err.at && <small className={css.err}>{err.at}</small>}
        </label>
      )}
      <label className={css.field}>
        <span>{T('Begrunnelse')}</span>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-invalid={err.reason ? true : undefined}
          data-testid="saker-pause-reason"
        >
          <option value="">{T('Velg en begrunnelse')}</option>
          {PAUSE_REASONS.map((r) => (
            <option key={r} value={r}>
              {T(r)}
            </option>
          ))}
        </select>
        {err.reason && <small className={css.err}>{err.reason}</small>}
      </label>
      <p className={css.preview} aria-live="polite" data-testid="saker-pause-net">
        {validMin
          ? T('Tid etter pause: {net}. Rettingen er ikke en godkjenning av timene.', {
              net: hoursMin(gross - n),
            })
          : T('Tid etter pause regnes ut når en gyldig pause er skrevet inn.')}
      </p>
      <Problem p={problem} />
    </Frame>
  );
}

/**
 * «Juster neste vakt» (S11): both shifts and the rest rule the plan is checked with (the law's 11 hours, core); the later shift's new start must leave the
 * rule's hours after the earlier one ends. Saved as a change to the plan. What happens next is said as the
 * domain does it: a draft stays unpublished; a published shift's person is told of the move by the plan's own
 * notice when it is saved (services/notifyStories.js NT3), as any move of a published shift is today.
 */
export function PlanDialog({
  c,
  open,
  busy,
  problem,
  onClose,
  onSave,
}: {
  c: ReviewCase;
  open: boolean;
  busy: boolean;
  problem: WriteProblem | null;
  onClose: () => void;
  onSave: (v: { start: string; end: string }) => void;
}) {
  const r = c.rest;
  const earliest = r ? new Date(Date.parse(r.before.end) + r.need * 36e5).toISOString() : '';
  const day = oslo(r?.after.start)?.date ?? '';
  const crosses = r ? (oslo(r.after.end)?.date ?? '') > day : false;
  const fromT = clock(r?.after.start);
  const toT = clock(r?.after.end);
  const [from, setFrom] = useState(fromT);
  const [to, setTo] = useState(toT);
  const [err, setErr] = useState<string | null>(null);
  if (!r) return null;
  const dirty = from !== fromT || to !== toT;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d\d:\d\d$/.test(from) || !/^\d\d:\d\d$/.test(to)) return setErr(T('Skriv inn start og slutt.'));
    const start = osloISO(day, from);
    // a night shift ends on the next day; any other end must come after its start the same day
    const endDay = to <= from && crosses ? (oslo(r.after.end)?.date ?? day) : day;
    const end = osloISO(endDay, to);
    if (Date.parse(end) <= Date.parse(start)) return setErr(T('Slutt må være etter start.'));
    if (Date.parse(start) < Date.parse(earliest))
      return setErr(
        T('Med {n} timer hvile kan vakten tidligst starte {t}.', { n: r.need, t: `${clock(earliest)}` }),
      );
    setErr(null);
    onSave({ start, end });
  };
  return (
    <Frame
      open={open}
      title={T('Juster neste vakt')}
      sub={[c.person?.name, c.dateText].filter(Boolean).join(', ')}
      dirty={dirty}
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      testid="saker-plan"
      footer=<Button tone="pri" type="submit" disabled={busy} data-testid="saker-plan-save">
        {busy ? T('Lagrer …') : T('Lagre planendring')}
      </Button>
    >
      <Facts
        rows={[
          [T('Forrige vakt slutter'), `${clock(r.before.end)}`],
          [T('Hvileregel'), T('{n} t, AML § 10-8 (1)', { n: r.need })],
          [T('Tidligste start'), clock(earliest)],
        ]}
      />
      <div className={css.pair}>
        <label className={css.field}>
          <span>{T('Ny start')}</span>
          <input
            type="time"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="saker-plan-start"
          />
        </label>
        <label className={css.field}>
          <span>{T('Slutt')}</span>
          <input
            type="time"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="saker-plan-end"
          />
        </label>
      </div>
      {err && (
        <p className={css.err} role="alert">
          {err}
        </p>
      )}
      <p className={css.preview}>
        {r.after.published
          ? T(
              'Vakten er publisert. Når du lagrer, får den ansatte beskjed om den nye tiden, slik planen varsler i dag.',
            )
          : T(
              'Endringen lagres i planen som utkast. Publiser vaktplanen etterpå for å dele den med den ansatte.',
            )}
      </p>
      <Problem p={problem} />
    </Frame>
  );
}

/**
 * «Ta senere» (S14): when to follow up. The task moves to Utsatt and comes back under Å følge opp on that day; it is
 * not resolved or approved.
 */
export function DeferDialog({
  c,
  open,
  today,
  onClose,
  onDefer,
}: {
  c: ReviewCase;
  open: boolean;
  today: string;
  onClose: () => void;
  onDefer: (until: string, reason?: string) => void;
}) {
  const tomorrow = addDay(today, 1);
  const [until, setUntil] = useState(tomorrow);
  const [why, setWhy] = useState('');
  const [err, setErr] = useState<string | null>(null);
  return (
    <Frame
      open={open}
      title={T('Ta oppgaven senere')}
      sub={[c.what ?? c.title, c.person?.name].filter(Boolean).join(', ')}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (!/^\d{4}-\d\d-\d\d$/.test(until) || until <= today) return setErr(T('Velg en dag etter i dag.'));
        onDefer(until, why.trim() || undefined);
      }}
      testid="saker-defer"
      footer=<Button tone="pri" type="submit" data-testid="saker-defer-save">
        {T('Utsett oppgaven')}
      </Button>
    >
      <label className={css.field}>
        <span>{T('Når vil du følge opp?')}</span>
        <input
          type="date"
          min={tomorrow}
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          data-testid="saker-defer-date"
        />
        {err && <small className={css.err}>{err}</small>}
      </label>
      <label className={css.field}>
        <span>{T('Hvorfor venter du? (valgfritt)')}</span>
        <input
          type="text"
          maxLength={500}
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          data-testid="saker-defer-reason"
        />
      </label>
      <p className={css.preview}>
        {T(
          'Oppgaven flyttes til Utsatt og kommer tilbake under Å følge opp den dagen. Den blir ikke løst eller godkjent.',
        )}
      </p>
    </Frame>
  );
}

function addDay(d: string, n: number) {
  const t = new Date(`${d}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
}

/**
 * A worklist's approval from Saker (S10): every record named with a 44 px choice; only the chosen ones are sent.
 * Approval is not export to payroll, and the dialog says so.
 */
export function ApproveDialog({
  c,
  open,
  busy,
  problem,
  onClose,
  onApprove,
}: {
  c: ReviewCase;
  open: boolean;
  busy: boolean;
  problem: WriteProblem | null;
  onClose: () => void;
  onApprove: (ids: number[]) => void;
}) {
  const ids = c.approve ?? [];
  const [chosen, setChosen] = useState<Set<number>>(() => new Set(ids));
  const label = (id: number) => c.members?.find((m) => m.id === id)?.label ?? `#${id}`;
  const n = chosen.size;
  return (
    <Frame
      open={open}
      title={T('Godkjenn valgte registreringer')}
      sub={c.title}
      busy={busy}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (n) onApprove(ids.filter((id) => chosen.has(id)));
      }}
      testid="saker-approve"
      footer=<Button tone="pri" type="submit" disabled={busy || !n} data-testid="saker-approve-save">
        {busy
          ? T('Lagrer …')
          : n === 1
            ? T('Godkjenn 1 registrering')
            : T('Godkjenn {n} registreringer', { n })}
      </Button>
    >
      <p>
        {T('Du godkjenner {n} av {m} registreringer i arbeidslisten. Ingen andre registreringer endres.', {
          n,
          m: ids.length,
        })}
      </p>
      <ul className={css.records}>
        {ids.map((id) => (
          <li key={id}>
            <label>
              <input
                type="checkbox"
                checked={chosen.has(id)}
                onChange={(e) => {
                  const next = new Set(chosen);
                  if (e.target.checked) next.add(id);
                  else next.delete(id);
                  setChosen(next);
                }}
              />
              <span>{label(id)}</span>
            </label>
          </li>
        ))}
      </ul>
      <p className={css.preview}>
        {T('Godkjente registreringer kan inngå i lønnsgrunnlaget. Eksport til lønn er en egen handling.')}
      </p>
      <Problem p={problem} />
    </Frame>
  );
}

/**
 * An absence request's decision (S12): approve or reject, with a reason that goes into the history (and, for a
 * rejection, the employee's message, as Fravær sends it). The balance and the shifts are shown in the task above.
 */
export function DecisionDialog({
  c,
  ok,
  open,
  busy,
  problem,
  onClose,
  onDecide,
}: {
  c: ReviewCase;
  ok: boolean;
  open: boolean;
  busy: boolean;
  problem: WriteProblem | null;
  onClose: () => void;
  onDecide: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  return (
    <Frame
      open={open}
      title={ok ? T('Innvilg forespørselen') : T('Avslå forespørselen')}
      sub={[c.person?.name, c.dateText ?? c.dateLabel].filter(Boolean).join(', ')}
      dirty={!!reason}
      busy={busy}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim().length < 3) return setErr(T('Skriv en begrunnelse.'));
        setErr(null);
        onDecide(reason.trim());
      }}
      testid="saker-decision"
      footer=<Button
        tone={ok ? 'pri' : 'dgr'}
        type="submit"
        disabled={busy}
        data-testid="saker-decision-save"
      >
        {busy ? T('Lagrer …') : ok ? T('Bekreft innvilgelse') : T('Bekreft avslag')}
      </Button>
    >
      <label className={css.field}>
        <span>{T('Begrunnelse')}</span>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-invalid={err ? true : undefined}
          data-testid="saker-decision-reason"
        />
        {err && <small className={css.err}>{err}</small>}
      </label>
      <p className={css.preview}>
        {ok
          ? T(
              'Den ansatte får beskjed når du har svart. Dette er en beslutning om fraværet, ikke en lønnsgodkjenning.',
            )
          : T('Den ansatte får beskjed om avslaget med begrunnelsen. Ingen dager trekkes.')}
      </p>
      <Problem p={problem} />
    </Frame>
  );
}

/** «Slik fungerer Saker» (S20): what counts as one task, the four states, and what each action does and does not. */
export function HelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Frame
      open={open}
      title={T('Slik fungerer Saker')}
      onClose={onClose}
      testid="saker-help"
      cancel={false}
      footer={
        <Button tone="pri" onClick={onClose}>
          {T('Lukk')}
        </Button>
      }
    >
      <p>{T('Saker samler det du må ta stilling til fra planen, registreringene og lønnsgrunnlaget.')}</p>
      <ul className={css.help}>
        <li>
          <strong>{T('Én oppgave er én avgjørelse.')}</strong>{' '}
          {T(
            'Flere forhold ved samme vakt vises sammen. En arbeidsliste teller som én oppgave og viser registreringene for seg. Opplysninger uten handling telles ikke.',
          )}
        </li>
        <li>
          <strong>{T('Perioden og teamet vises alltid.')}</strong>{' '}
          {T('«Andre perioder» viser bare oppgaver utenfor perioden du har valgt. Teamet beholdes.')}
        </li>
        <li>
          <strong>{T('Å følge opp:')}</strong> {T('du kan ta en avgjørelse nå.')}{' '}
          <strong>{T('Venter:')}</strong> {T('et svar fra en ansatt mangler.')}{' '}
          <strong>{T('Utsatt:')}</strong> {T('du har valgt en dag å følge opp.')}{' '}
          <strong>{T('Ferdig:')}</strong> {T('avgjørelsen er lagret fra Saker på denne enheten.')}
        </li>
        <li>
          <strong>{T('En retting er ikke en godkjenning.')}</strong>{' '}
          {T('Godkjenning er ikke eksport til lønn, og en lagret planendring er ikke en publisering.')}
        </li>
        <li>
          <strong>{T('Arbeidslister beholder utvalget.')}</strong>{' '}
          {T(
            'Kontroll åpner med nøyaktig de registreringene oppgaven gjelder, og «Tilbake til saken» tar deg hit igjen.',
          )}
        </li>
      </ul>
      <p>
        {T(
          'Tastatur: Tab mellom kontroller, piltaster, Home og End mellom statusfanene, J og K mellom oppgaver, Escape for å gå et nivå opp eller lukke.',
        )}
      </p>
    </Frame>
  );
}

/** «Endre» (S03): the week and the team Saker counts; the plan follows, so both always say the same. */
export function ScopeDialog({
  open,
  weeks,
  monday,
  teams,
  teamId,
  onClose,
  onApply,
}: {
  open: boolean;
  weeks: { monday: string; label: string }[];
  monday: string;
  teams: { id: number; name: string }[];
  teamId: number | null;
  onClose: () => void;
  onApply: (v: { monday: string; teamId: number | null }) => void;
}) {
  const [w, setW] = useState(monday);
  const [t, setT] = useState(teamId == null ? '' : String(teamId));
  useEffect(() => {
    if (open) {
      setW(monday);
      setT(teamId == null ? '' : String(teamId));
    }
  }, [open, monday, teamId]);
  return (
    <Frame
      open={open}
      title={T('Periode og team')}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        onApply({ monday: w, teamId: t ? Number(t) : null });
      }}
      testid="saker-scope"
      footer=<Button tone="pri" type="submit" data-testid="saker-scope-save">
        {T('Vis oppgavene')}
      </Button>
    >
      <label className={css.field}>
        <span>{T('Uke')}</span>
        <select value={w} onChange={(e) => setW(e.target.value)} data-testid="saker-scope-week">
          {weeks.map((x) => (
            <option key={x.monday} value={x.monday}>
              {x.label}
            </option>
          ))}
        </select>
      </label>
      {teams.length > 0 && (
        <label className={css.field}>
          <span>{T('Team')}</span>
          <select value={t} onChange={(e) => setT(e.target.value)} data-testid="saker-scope-team">
            <option value="">{T('Alle team')}</option>
            {teams.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </Frame>
  );
}
